import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import type { CallLog, CallDisposition } from '@/types'

interface CallContext {
  entity: {
    who_id?: string
    who_type?: string
    who_name?: string
    what_id?: string
    what_type?: string
    what_name?: string
    phone_number: string
    formatted_phone?: string
  } | null
}

interface ActiveCall {
  isActive: boolean
  startTime: Date | null
  elapsedSeconds: number
  context: CallContext | null
}

interface CallProviderState {
  activeCall: ActiveCall
  startCall: (context: CallContext) => void
  endCall: () => { duration: number; startedAt: string; endedAt: string } | null
  cancelCall: () => void
  isCallDialogOpen: boolean
  openCallDialog: (context: CallContext) => void
  closeCallDialog: () => void
  callDialogContext: CallContext | null
  callDialogDefaults: Partial<CallLog> | null
}

const CallProviderContext = createContext<CallProviderState | null>(null)

export function useCallProvider() {
  const context = useContext(CallProviderContext)
  if (!context) {
    throw new Error('useCallProvider must be used within a CallProvider')
  }
  return context
}

// Auto-timeout after 2 hours (in seconds)
const MAX_CALL_DURATION = 2 * 60 * 60

export function CallProvider({ children }: { children: React.ReactNode }) {
  const [activeCall, setActiveCall] = useState<ActiveCall>({
    isActive: false,
    startTime: null,
    elapsedSeconds: 0,
    context: null,
  })
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false)
  const [callDialogContext, setCallDialogContext] = useState<CallContext | null>(null)
  const [callDialogDefaults, setCallDialogDefaults] = useState<Partial<CallLog> | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Timer effect
  useEffect(() => {
    if (activeCall.isActive && activeCall.startTime) {
      timerRef.current = setInterval(() => {
        setActiveCall((prev) => {
          const elapsed = Math.floor(
            (Date.now() - (prev.startTime?.getTime() || Date.now())) / 1000
          )

          // Auto-timeout
          if (elapsed >= MAX_CALL_DURATION) {
            if (timerRef.current) clearInterval(timerRef.current)
            return { ...prev, isActive: false, elapsedSeconds: elapsed }
          }

          return { ...prev, elapsedSeconds: elapsed }
        })
      }, 1000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [activeCall.isActive, activeCall.startTime])

  const startCall = useCallback((context: CallContext) => {
    setActiveCall({
      isActive: true,
      startTime: new Date(),
      elapsedSeconds: 0,
      context,
    })
  }, [])

  const endCall = useCallback(() => {
    if (!activeCall.isActive || !activeCall.startTime) return null

    const endTime = new Date()
    const duration = Math.floor((endTime.getTime() - activeCall.startTime.getTime()) / 1000)

    const result = {
      duration,
      startedAt: activeCall.startTime.toISOString(),
      endedAt: endTime.toISOString(),
    }

    setActiveCall({
      isActive: false,
      startTime: null,
      elapsedSeconds: 0,
      context: null,
    })

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    return result
  }, [activeCall])

  const cancelCall = useCallback(() => {
    setActiveCall({
      isActive: false,
      startTime: null,
      elapsedSeconds: 0,
      context: null,
    })
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const openCallDialog = useCallback((context: CallContext) => {
    setCallDialogContext(context)
    setCallDialogDefaults({
      phone_number: context.entity?.phone_number || '',
      formatted_phone: context.entity?.formatted_phone,
      who_id: context.entity?.who_id,
      who_type: context.entity?.who_type,
      what_id: context.entity?.what_id,
      what_type: context.entity?.what_type,
      direction: 'Outbound' as const,
      disposition: 'Connected' as CallDisposition,
    })
    setIsCallDialogOpen(true)
  }, [])

  const closeCallDialog = useCallback(() => {
    setIsCallDialogOpen(false)
    setCallDialogContext(null)
    setCallDialogDefaults(null)
  }, [])

  return (
    <CallProviderContext.Provider
      value={{
        activeCall,
        startCall,
        endCall,
        cancelCall,
        isCallDialogOpen,
        openCallDialog,
        closeCallDialog,
        callDialogContext,
        callDialogDefaults,
      }}
    >
      {children}
    </CallProviderContext.Provider>
  )
}
